import React from 'react';
import { MapPinIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

// Simple location display component that doesn't rely on external map services
const TourMap = ({ location, title }) => {
  // Default location name if none provided
  const defaultLocationName = 'Tour Location';
  
  // Extract location name from the location object or use title
  const getLocationName = () => {
    if (typeof location === 'string') {
      return location;
    }
    
    if (location && typeof location === 'object') {
      if (location.name) return location.name;
      if (location.city) return location.city;
      if (location.country) return location.country;
      
      // If we have coordinates, format them nicely
      if ((location.lat && location.lng) || (location.latitude && location.longitude)) {
        const lat = location.lat || location.latitude;
        const lng = location.lng || location.longitude;
        return `Coordinates: ${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
      }
    }
    
    return title || defaultLocationName;
  };
  
  // Get a formatted location string with additional details if available
  const getFormattedLocation = () => {
    if (typeof location === 'string') {
      return location;
    }
    
    if (location && typeof location === 'object') {
      const parts = [];
      
      if (location.name) parts.push(location.name);
      if (location.city && !parts.includes(location.city)) parts.push(location.city);
      if (location.region && !parts.includes(location.region)) parts.push(location.region);
      if (location.country && !parts.includes(location.country)) parts.push(location.country);
      
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
    
    return getLocationName();
  };
  
  return (
    <div className="p-6">
      <h3 className="text-xl font-bold mb-6">Tour Location</h3>
      
      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
        {/* Location info card */}
        <div className="p-6">
          <div className="flex items-start">
            <div className="bg-primary bg-opacity-10 p-3 rounded-full mr-4">
              <MapPinIcon className="h-8 w-8 text-primary" />
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-2">{getLocationName()}</h4>
              <p className="text-gray-600 mb-4">{getFormattedLocation()}</p>
              
              <div className="flex items-center text-sm text-gray-500">
                <GlobeAltIcon className="h-5 w-5 mr-2" />
                <span>Interactive map will be available soon</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Visual representation */}
        <div className="bg-gradient-to-b from-blue-50 to-green-50 p-10 flex justify-center items-center">
          <div className="relative">
            <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center">
              <div className="w-24 h-24 bg-blue-200 rounded-full flex items-center justify-center">
                <div className="w-16 h-16 bg-blue-300 rounded-full flex items-center justify-center">
                  <div className="absolute">
                    <div className="w-6 h-6 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-6 h-6 bg-red-500 bg-opacity-30 rounded-full absolute top-0 left-0 animate-ping"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-md">
              <span className="font-medium text-gray-800">{getLocationName()}</span>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-gray-500 mt-4">
        Note: This shows the approximate location of the tour. The exact meeting point will be provided after booking.
      </p>
    </div>
  );
};

export default TourMap;
